// @snake-case-api Terraform variable names are a versioned external contract.
export interface AzureNetwork {
  networkCidr: string
  subnetCidr: string
  dbSubnetCidr: string
  dfsSubnetCidr: string
}

export const defaultAzureNetwork: AzureNetwork = {
  networkCidr: '10.0.0.0/16',
  subnetCidr: '10.0.0.0/25',
  dbSubnetCidr: '10.0.0.128/25',
  dfsSubnetCidr: '10.0.1.0/28',
}

export function ipv4Prefix(value: string): { start: number; end: number; bits: number } | null {
  const match = /^(\d+)\.(\d+)\.(\d+)\.(\d+)\/(\d+)$/.exec(value)
  if (!match) return null
  const parts = match.slice(1, 5).map(Number)
  const bits = Number(match[5])
  if (parts.some((n) => n > 255) || bits > 32) return null
  const address = parts.reduce((n, octet) => n * 256 + octet, 0)
  const size = 2 ** (32 - bits)
  if (address % size !== 0) return null
  return { start: address, end: address + size - 1, bits }
}

/** Validate all reserved subnets, including currently disabled features. */
export function validAzureNetwork(network: AzureNetwork): boolean {
  const parent = ipv4Prefix(network.networkCidr)
  const children = [network.subnetCidr, network.dbSubnetCidr, network.dfsSubnetCidr].map(ipv4Prefix)
  if (!parent || parent.bits < 8 || parent.bits > 29) return false
  return children.every(
    (child, i) =>
      child &&
      child.bits <= 29 &&
      child.start >= parent.start &&
      child.end <= parent.end &&
      children
        .slice(0, i)
        .every((other) => other && (child.end < other.start || child.start > other.end))
  )
}

/** Deterministic suggestion only; never silently rewrites an existing allocation. */
export function suggestAzureNetwork(cidr: string): AzureNetwork | null {
  const prefix = ipv4Prefix(cidr)
  if (!prefix || prefix.bits > 23 || prefix.bits < 8) return null
  const ip = (n: number) =>
    [24, 16, 8, 0].map((shift) => Math.floor(n / 2 ** shift) % 256).join('.')
  return {
    networkCidr: cidr,
    subnetCidr: `${ip(prefix.start)}/25`,
    dbSubnetCidr: `${ip(prefix.start + 128)}/25`,
    dfsSubnetCidr: `${ip(prefix.start + 256)}/28`,
  }
}

export function azureNetworkVariables(network: AzureNetwork): Record<string, string> {
  return {
    network_cidr: network.networkCidr,
    subnet_cidr: network.subnetCidr,
    db_subnet_cidr: network.dbSubnetCidr,
    dfs_subnet_cidr: network.dfsSubnetCidr,
  }
}

export function readAzureNetwork(config: Record<string, unknown>): AzureNetwork {
  const snapshot = (config['lastApplyVariables'] ?? config['last_apply_variables'] ?? {}) as Record<
    string,
    unknown
  >
  const read = (camel: keyof AzureNetwork, snake: string) =>
    String(
      snapshot[camel] ??
        snapshot[snake] ??
        config[camel] ??
        config[snake] ??
        defaultAzureNetwork[camel]
    )
  return {
    networkCidr: read('networkCidr', 'network_cidr'),
    subnetCidr: read('subnetCidr', 'subnet_cidr'),
    dbSubnetCidr: read('dbSubnetCidr', 'db_subnet_cidr'),
    dfsSubnetCidr: read('dfsSubnetCidr', 'dfs_subnet_cidr'),
  }
}
